import React, { useMemo } from "react";

/*
	Grafica de barras del tablero, en HTML puro.

	Antes era un SVG con viewBox, y ese fue el error: el SVG se escala al ancho
	del contenedor, asi que su texto se dibujaba a un tamano y luego se reducia.
	El navegador rasteriza distinto un texto escalado que uno a tamano nativo, y
	el espaciado entre letras terminaba viendose distinto al del resto de la
	pantalla aunque fuera la misma fuente y el mismo tamano nominal.

	Con divs, cada texto es texto normal a su tamano real y cae exactamente
	igual que el resto de la interfaz. Las barras son cajas con alto en pixeles.
*/

export interface BarDatum {
	label: string;
	total: number;
}

interface Props {
	titulo: string;
	datos: BarDatum[];
	/* "normal" pinta naranja; "urgente" pinta oro */
	tono: "normal" | "urgente";
	rotuloHoy: string;
	rangos: string[];
	rangoActivo: string;
	onRango: (r: string) => void;
	cargando: boolean;
	idSelect: string;
}

/* alto del area de barras, en pixeles reales */
const ALTO = 118;
/* una barra en 0 conserva un hilo visible para que se vea que el dia existe */
const ALTO_CERO = 3;

const BarChart = ({
	titulo, datos, tono, rotuloHoy,
	rangos, rangoActivo, onRango, cargando, idSelect,
}: Props) => {
	const total = useMemo(() => datos.reduce((a, d) => a + d.total, 0), [datos]);

	const barras = useMemo(() => {
		if (datos.length === 0) return [];
		const max = Math.max(...datos.map((d) => d.total), 1);
		return datos.map((d, i) => ({
			...d,
			alto: d.total === 0 ? ALTO_CERO : Math.max(Math.round((d.total / max) * ALTO), 6),
			esUltimo: i === datos.length - 1,
		}));
	}, [datos]);

	return (
		<div className="wt-chart">
			<header>
				<h3>{titulo}</h3>
				<span className="wt-total">
					<b className={cargando ? "wt-skeleton" : undefined}>{cargando ? "00" : total}</b>
					en el periodo
				</span>
				<select
					id={idSelect}
					className="wt-range"
					value={rangoActivo}
					onChange={(e) => onRango(e.target.value)}
					disabled={cargando}
					aria-label={`Rango de ${titulo.toLowerCase()}`}
				>
					{rangos.map((r) => (
						<option key={r} value={r}>{r}</option>
					))}
				</select>
			</header>

			{barras.length === 0 ? (
				<div className="wt-vacio">{cargando ? "Cargando…" : "Sin movimientos en el periodo"}</div>
			) : (
				<div className="wt-plot" data-tono={tono} role="img"
					aria-label={`${titulo}: ${datos.map((d) => `${d.label} ${d.total}`).join(", ")}`}>
					{barras.map((b) => (
						<div
							key={b.label}
							className="wt-col"
							data-hoy={b.esUltimo ? "si" : undefined}
						>
							<span className="wt-val">{b.total}</span>
							<div className="wt-bar" style={{ height: `${b.alto}px` }} />
							<span className="wt-xlabel">{b.esUltimo ? rotuloHoy : b.label}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default BarChart;
